import { useMemo, useSyncExternalStore } from "react";
import {
  queueEntryKey,
  type GroomingQueueEntry,
  type GroomingQueueState,
  type GroomingToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import {
  getQueue,
  parseQueueState,
  queueActionsFor,
  queueBridgeOf,
  UNSUPPORTED_QUEUE_ACTIONS,
  type GroomingQueueActions,
} from "@/lib/toolbox/queue";

/**
 * The grooming run queue as SpecTree Toolbox reports it (epic
 * PI-SPEC-SW-E284, feature PI-SPEC-SW-1439).
 *
 * The queue is client state owned by the app on the person's computer, not
 * server state: it is read with `grooming.getQueue()` once and then replaced
 * by every `queue-changed` event. So it is held in a small module-level store
 * read with `useSyncExternalStore` (no TanStack Query, no fetch). One store
 * per bridge is shared by every component that reads the queue (the Queue
 * page, the icon rail's badge, the item and backlog markers), so there is a
 * single bridge subscription; it is dropped when the last reader unmounts.
 */

interface QueueStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => GroomingQueueState | null;
}

const stores = new WeakMap<GroomingToolsetBridge, QueueStore>();

/**
 * Build the store for one grooming bridge. The first subscriber starts
 * `getQueue()` and the `queue-changed` subscription; the last one to leave
 * stops the subscription (the last state stays as the snapshot until the
 * next subscriber reloads it). A `getQueue()` answer that arrives after a
 * `queue-changed` event is older than that event and is ignored.
 */
function createQueueStore(grooming: GroomingToolsetBridge): QueueStore {
  let state: GroomingQueueState | null = null;
  const listeners = new Set<() => void>();
  let unsubscribeBridge: (() => void) | null = null;
  let generation = 0;

  const publish = (next: GroomingQueueState) => {
    state = next;
    for (const listener of listeners) listener();
  };

  const start = () => {
    generation += 1;
    const loadGeneration = generation;
    unsubscribeBridge = grooming.onEvent((raw: unknown) => {
      if (typeof raw !== "object" || raw === null || (raw as { type?: unknown }).type !== "queue-changed") return;
      const next = parseQueueState((raw as { queue?: unknown }).queue);
      if (next === null) return;
      generation += 1;
      publish(next);
    });
    void getQueue(grooming).then((loaded) => {
      if (loaded === null || loadGeneration !== generation || unsubscribeBridge === null) return;
      publish(loaded);
    });
  };

  const stop = () => {
    unsubscribeBridge?.();
    unsubscribeBridge = null;
    generation += 1;
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      if (listeners.size === 1) start();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stop();
      };
    },
    getSnapshot: () => state,
  };
}

function storeFor(grooming: GroomingToolsetBridge): QueueStore {
  let store = stores.get(grooming);
  if (!store) {
    store = createQueueStore(grooming);
    stores.set(grooming, store);
  }
  return store;
}

const noSubscribe = () => () => undefined;
const noSnapshot = () => null;

export interface GroomingQueueView {
  /** True inside a contract-3 SpecTree Toolbox (see `queueSupported`). */
  supported: boolean;
  /** The queue the app reported last; null while loading, and always null when not supported. */
  state: GroomingQueueState | null;
  /** The queue actions; when not supported every call resolves as a failure. */
  actions: GroomingQueueActions;
}

/** The run queue for a component: whether the app has one, its last state and the actions. */
export function useGroomingQueue(): GroomingQueueView {
  const bridge = useToolboxBridge();
  const grooming = queueBridgeOf(bridge);
  const store = grooming ? storeFor(grooming) : null;
  const state = useSyncExternalStore(store?.subscribe ?? noSubscribe, store?.getSnapshot ?? noSnapshot);
  const actions = useMemo(() => (grooming ? queueActionsFor(grooming) : UNSUPPORTED_QUEUE_ACTIONS), [grooming]);
  return { supported: grooming !== null, state, actions };
}

/** One tracker item's place in the queue. */
export interface QueueEntryForItem {
  /** The queued or running entry for the item. */
  entry: GroomingQueueEntry;
  /** `running`, or `queued` with its 1-based place in Up next. */
  status: "running" | "queued";
  /** 1-based place in Up next ("Queued · #3"); null while running. */
  position: number | null;
}

/**
 * Find a tracker item's queued or running `groom-item` entry by its
 * de-duplication key (the contract's `queueEntryKey`). Pure; exported for tests.
 */
export function queueEntryForItem(
  state: GroomingQueueState | null,
  project: string,
  itemId: string
): QueueEntryForItem | null {
  if (!state || project.trim() === "" || itemId.trim() === "") return null;
  const key = queueEntryKey("groom-item", project, itemId);
  const running = state.running.find((entry) => entry.key === key);
  if (running) return { entry: running, status: "running", position: null };
  const index = state.upNext.findIndex((entry) => entry.key === key);
  const queued = index >= 0 ? state.upNext[index] : undefined;
  return queued ? { entry: queued, status: "queued", position: index + 1 } : null;
}

/**
 * The queue entry for one tracker item, so its Groom button can say
 * "Queued · #3" or "Running" instead. Null when the item is not queued or
 * running, while loading, or when the app has no queue.
 */
export function useQueueEntryFor(
  project: string | null | undefined,
  itemId: string | null | undefined
): QueueEntryForItem | null {
  const { state } = useGroomingQueue();
  return useMemo(
    () => (project && itemId ? queueEntryForItem(state, project, itemId) : null),
    [state, project, itemId]
  );
}
